import { useLocation, useNavigate } from "react-router-dom";

import { cn, formatNumber } from "@/lib/cn";
import { useCost, useLogout, useProjects, useSession } from "@/api/queries";
import { initialOf, roleLabel } from "@/lib/session";
import { useUiStore } from "@/store/ui";
import { STUDIO_NAV } from "@/views/meta";

const QUOTA_TOTAL = 10_000;

/**
 * 工作台侧栏（原型 `.sidebar`，232px）。
 * 观众侧视图由 `body.is-platform-view` 整体隐藏，与原型一致。
 */
export function Sidebar() {
  const navigate = useNavigate();
  const location = useLocation();
  const locationView = location.pathname.split("/").filter(Boolean)[1] ?? "overview";
  const projectId = useUiStore((state) => state.projectId);
  const setProjectId = useUiStore((state) => state.setProjectId);
  const projects = useProjects();
  const cost = useCost(projectId);
  const session = useSession();
  const logout = useLogout();
  const user = session.data ?? null;

  const remaining = cost.data?.remaining ?? 6420;
  const usedRatio = ((QUOTA_TOTAL - remaining) / QUOTA_TOTAL) * 100;

  return (
    <aside className="sidebar" aria-label="产品导航">
      <div className="brand-block">
        <div className="brand-mark" aria-hidden="true">
          <span></span>
          <span></span>
          <span></span>
        </div>
        <div>
          <strong>专业创作台</strong>
          <small>STORY PRODUCTION</small>
        </div>
      </div>

      <div className="project-switcher">
        <label htmlFor="project-select">当前项目</label>
        <div className="select-wrap">
          <select
            id="project-select"
            aria-label="选择项目"
            value={projectId}
            onChange={(event) => setProjectId(event.target.value)}
          >
            {(projects.data ?? []).map((project) => (
              <option key={project.id} value={project.id}>
                {project.name}
              </option>
            ))}
          </select>
          <span aria-hidden="true">⌄</span>
        </div>
      </div>

      <nav className="primary-nav" aria-label="主要功能">
        {STUDIO_NAV.map((item) => (
          <button
            key={item.view}
            type="button"
            className={cn("nav-item", item.view === locationView && "is-active")}
            aria-current={item.view === locationView ? "page" : undefined}
            onClick={() => navigate(`/studio/${item.view}`)}
          >
            <span className="nav-symbol" aria-hidden="true">
              {item.symbol}
            </span>
            <span>{item.label}</span>
            {item.count ? (
              <span className={cn("nav-count", item.alert && "is-alert")}>{item.count}</span>
            ) : null}
          </button>
        ))}
      </nav>

      <div className="sidebar-spacer"></div>

      <div className="studio-signal">
        <span aria-hidden="true">↙</span>
        <div>
          <strong>286条观众信号</strong>
          <small>等待项目编剧筛选</small>
        </div>
      </div>

      <div className="quota-mini" aria-label="本月生成额度">
        <div>
          <span>生成额度</span>
          <strong>
            <span id="quota-value">{formatNumber(remaining)}</span> / {formatNumber(QUOTA_TOTAL)}
          </strong>
        </div>
        <div className="meter">
          <span style={{ width: `${usedRatio.toFixed(1)}%` }}></span>
        </div>
        <small>文本、图像与视频共用</small>
      </div>

      <button
        className="profile"
        type="button"
        title="退出登录"
        disabled={logout.isPending || !user}
        onClick={() => logout.mutate(undefined, { onSuccess: () => navigate("/") })}
      >
        <span className="avatar avatar-founder">{user ? initialOf(user) : "—"}</span>
        <span>
          <strong>{user ? user.display_name || user.email : "未登录"}</strong>
          <small>{roleLabel(user)} · 退出登录</small>
        </span>
        <span aria-hidden="true">•••</span>
      </button>
    </aside>
  );
}
